import {
  Mail,
  Play,
  Plus,
  RefreshCw,
  XCircle,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import useSWR from 'swr'

import { Clipboard } from '@/components/Clipboard'
import { Money, StatusBadge } from '@/components/ds'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { apiFetcher } from '@/data/fetcher'
import { AdminAccountSubscriptionFormModal } from '@/forms/AdminAccountSubscriptionForm/AdminAccountSubscriptionFormModal'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { formatDate } from '@/lib/formats/formatDate'
import {
  AdminSubscription,
  AdminSubscriptionListResponse,
} from '@/types/AdminSubscription'

import { AdminSection } from './AdminSection'

const statusTone: Record<AdminSubscription['status'], string> = {
  active: 'paid',
  requested: 'pending',
  scheduled: 'sent',
  expired: 'draft',
  revoked: 'cancelled',
}

type PendingAction =
  | { type: 'activate'; subscription: AdminSubscription }
  | { type: 'revoke'; subscription: AdminSubscription }

export function AdminAccountSubscriptions({
  accountId,
}: {
  accountId: string
}) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'admin.accounts.detail.subscriptions',
  })

  const [createOpen, setCreateOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)
  const [actionLoading, setActionLoading] = useState(false)

  const {
    data,
    isLoading,
    mutate,
  } = useSWR<AdminSubscriptionListResponse>(
    `/api/v2/admin/accounts/${accountId}/subscriptions`,
    apiFetcher,
  )

  const subscriptions = data?.data ?? []

  const activeSubscription = useMemo(
    () => subscriptions.find((subscription) => subscription.status === 'active'),
    [subscriptions],
  )

  const runAction = async (
    subscriptionId: string,
    path: string,
    successMessage: string,
  ) => {
    setActionLoading(true)

    try {
      await API.post(
        `/api/v2/admin/accounts/${accountId}/subscriptions/${subscriptionId}/${path}`,
      )
      toast.success(successMessage)
      await mutate()
    } catch (error: unknown) {
      toast.error(t('actions.error'), {
        description: extractErrorMessage(error),
      })
    } finally {
      setActionLoading(false)
      setPendingAction(null)
    }
  }

  const confirmAction = () => {
    if (!pendingAction) {
      return
    }

    const { subscription } = pendingAction

    if (pendingAction.type === 'activate') {
      void runAction(subscription.id, 'activate', t('actions.activateSuccess'))
      return
    }

    void runAction(subscription.id, 'revoke', t('actions.revokeSuccess'))
  }

  return (
    <AdminSection
      title={t('title')}
      description={activeSubscription ? `Aktivní tarif ${activeSubscription.plan.name}` : 'Žádné aktivní předplatné'}
      action={
        <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
          <Plus />
          {t('create.button')}
        </Button>
      }
    >
      {isLoading && <Skeleton className="h-24 w-full rounded-lg" />}

      {!isLoading && subscriptions.length === 0 && (
        <div className="rounded-lg border border-dashed px-6 py-8 text-center text-sm text-muted-foreground">{t('empty')}</div>
      )}

      {!isLoading && subscriptions.length > 0 && (
        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>{t('columns.plan')}</TableHead>
                <TableHead className="hidden w-32 md:table-cell">{t('columns.status')}</TableHead>
                <TableHead className="hidden w-48 md:table-cell">Období</TableHead>
                <TableHead className="text-right md:w-32">{t('columns.price')}</TableHead>
                <TableHead className="text-right">{t('columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions.map((subscription) => (
                <TableRow key={subscription.id}>
                  <TableCell className="max-w-0 md:max-w-none">
                    <p className="truncate font-medium md:overflow-visible">{subscription.plan.name}</p>
                    <Clipboard value={subscription.id}>
                      <span className="block truncate font-mono text-xs text-muted-foreground md:inline">{subscription.id}</span>
                    </Clipboard>
                    <div className="mt-1 md:hidden">
                      <StatusBadge status={statusTone[subscription.status]} label={t(`status.${subscription.status}`)} />
                    </div>
                    <p className="truncate text-xs text-muted-foreground tabular md:hidden">
                      {subscription.startDate ? formatDate(new Date(subscription.startDate)) : '-'} až{' '}
                      {subscription.endDate ? formatDate(new Date(subscription.endDate)) : '-'}
                    </p>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <StatusBadge status={statusTone[subscription.status]} label={t(`status.${subscription.status}`)} />
                  </TableCell>
                  <TableCell className="hidden tabular md:table-cell">
                    {subscription.startDate ? formatDate(new Date(subscription.startDate)) : '-'}
                    <span className="text-muted-foreground"> až </span>
                    {subscription.endDate ? formatDate(new Date(subscription.endDate)) : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Money amount={subscription.price.amount} currency={subscription.price.currency} />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap justify-end gap-1.5">
                      {subscription.status === 'requested' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={actionLoading}
                            onClick={() => setPendingAction({
                              type: 'activate',
                              subscription,
                            })}
                          >
                            <Play />
                            {t('actions.activate')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={actionLoading}
                            onClick={() => setPendingAction({
                              type: 'revoke',
                              subscription,
                            })}
                          >
                            <XCircle />
                            {t('actions.revoke')}
                          </Button>
                          {subscription.unpaidOrder && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={actionLoading}
                              onClick={() => {
                                void runAction(
                                  subscription.id,
                                  'payment-emails',
                                  t('actions.emailSuccess'),
                                )
                              }}
                            >
                              <Mail />
                              {t('actions.sendEmail')}
                            </Button>
                          )}
                        </>
                      )}

                      {subscription.status === 'scheduled' && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={actionLoading}
                          onClick={() => setPendingAction({
                            type: 'revoke',
                            subscription,
                          })}
                        >
                          <XCircle />
                          {t('actions.revoke')}
                        </Button>
                      )}

                      {subscription.status === 'active' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={actionLoading}
                            onClick={() => setPendingAction({
                              type: 'revoke',
                              subscription,
                            })}
                          >
                            <XCircle />
                            {t('actions.revoke')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={actionLoading}
                            onClick={() => {
                              void runAction(
                                subscription.id,
                                'prolong',
                                t('actions.prolongSuccess'),
                              )
                            }}
                          >
                            <RefreshCw />
                            {t('actions.prolong')}
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AdminAccountSubscriptionFormModal
        accountId={accountId}
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => {
          void mutate()
        }}
      />

      <AlertDialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingAction(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingAction?.type === 'activate'
                ? t('confirm.activate.title')
                : t('confirm.revoke.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAction?.type === 'activate' && activeSubscription
                && activeSubscription.id !== pendingAction.subscription.id
                ? `Aktivní předplatné ${activeSubscription.plan.name} (${
                  activeSubscription.startDate ? formatDate(new Date(activeSubscription.startDate)) : '-'
                } až ${
                  activeSubscription.endDate ? formatDate(new Date(activeSubscription.endDate)) : '-'
                }) bude nejdříve zrušeno. Poté se aktivuje vybrané předplatné.`
                : pendingAction?.type === 'activate'
                  ? t('confirm.activate.description')
                  : t('confirm.revoke.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>
              {t('form.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction disabled={actionLoading} onClick={confirmAction}>
              {pendingAction?.type === 'activate'
                ? t('actions.activate')
                : t('actions.revoke')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminSection>
  )
}
